import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  AlertCircle,
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  Maximize2,
  Paperclip,
  Scale,
  Square,
  X
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { AssistantByline } from "@/features/agents/components/AssistantByline";
import { cn } from "@/lib/utils";

import {
  EXTRACTION_FAILED,
  EXTRACTION_READY,
  type CaseyAttachment,
  type CaseyProposal,
  type ProposalDecision
} from "../types";
import type { CaseyUIMessage } from "../hooks/useCaseyChat";
import { useCitationDocument } from "../context/CitationDocumentsContext";
import { useCitationViewer } from "../context/CitationViewerContext";
import { FileTypeIcon } from "../../../components/common/FileTypeIcon";
import { attorneyLabel, referralWho } from "../referral";
import { clientFacingError } from "./clientFacingError";
import { ProposalCard } from "./ProposalCard";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  assistantName: string;
  attorneyName?: string | null;
  lawFirmName?: string | null;
  messages: CaseyUIMessage[];
  isStreaming: boolean;
  /** The conversation so far is still being read back from the server. */
  isRestoring: boolean;
  error?: Error;
  onClearError: () => void;
  onSend: (text: string) => void;
  onStop: () => void;
  attachments: CaseyAttachment[];
  onUpload: (file: File) => void;
  uploadError?: string | null;
  decisions: Record<string, ProposalDecision>;
  onAcceptProposal: (proposalId: string) => void;
  onRejectProposal: (proposalId: string) => void;
  onAcceptAll: (proposalIds: string[]) => void;
};

/** The round ink control the composer and the jump-to-latest button share. */
const INK_ROUND_BUTTON = "rounded-full bg-fg text-elevated hover:bg-fg/85";

/** Opening prompts, phrased as things a client would actually say. */
const SUGGESTIONS = [
  "What does this question mean?",
  "Can you explain this in Spanish?",
  "I have my passport — can you fill in what you can?"
];

export function CaseyPanel({
  isOpen,
  onClose,
  assistantName,
  attorneyName,
  lawFirmName,
  messages,
  isStreaming,
  isRestoring,
  error,
  onClearError,
  onSend,
  onStop,
  attachments,
  onUpload,
  uploadError,
  decisions,
  onAcceptProposal,
  onRejectProposal,
  onAcceptAll
}: Props) {
  const [draft, setDraft] = useState("");
  const [isAtBottom, setIsAtBottom] = useState(true);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const scrollToBottom = useCallback((behavior: ScrollBehavior = "smooth") => {
    const node = scrollRef.current;
    if (!node) return;
    node.scrollTo({ top: node.scrollHeight, behavior });
    setIsAtBottom(true);
  }, []);

  // Follow the stream, since a reply that scrolls out of view reads as a stall
  // -- but only while the client is already at the bottom. Yanking them back
  // down mid-reply would stop them re-reading the answer they scrolled up for,
  // which is why there is a button to come back instead.
  useEffect(() => {
    const node = scrollRef.current;
    if (!node || !isAtBottom) return;
    node.scrollTop = node.scrollHeight;
  }, [messages, isStreaming, isAtBottom]);

  useEffect(() => {
    if (!isOpen) return;
    textareaRef.current?.focus();
    scrollToBottom("auto");
  }, [isOpen, scrollToBottom]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (isOpen && event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isOpen, onClose]);

  const submit = useCallback(() => {
    const text = draft.trim();
    if (!text || isStreaming) return;
    onSend(text);
    setDraft("");
    // Asking something while scrolled up should bring you back down to it.
    scrollToBottom();
  }, [draft, isStreaming, onSend, scrollToBottom]);

  const handleFiles = useCallback(
    (files: FileList | null) => {
      if (!files) return;
      for (const file of Array.from(files)) onUpload(file);
    },
    [onUpload]
  );

  const handleScroll = useCallback(() => {
    const node = scrollRef.current;
    if (!node) return;
    // A few pixels of slack: fractional scroll heights mean an exact comparison
    // reads as "scrolled up" when the client is sitting at the bottom.
    const fromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
    setIsAtBottom(fromBottom <= 24);
  }, []);

  const hasConversation = messages.length > 0;

  return (
    <>
      {isOpen ? (
        <button
          type="button"
          aria-label="Close assistant"
          tabIndex={-1}
          onClick={onClose}
          className="fixed inset-0 z-1450 cursor-default bg-black/40 md:pointer-events-none md:bg-transparent"
        />
      ) : null}

      <aside
        aria-hidden={!isOpen}
        aria-label={assistantName}
        className={cn(
          "fixed inset-x-0 bottom-0 z-1500 flex max-h-[85vh] flex-col overflow-hidden rounded-t-xl bg-elevated shadow-dialog ring-1 ring-line-2 transition-transform duration-200 ease-out md:inset-x-auto md:right-6 md:bottom-6 md:h-[min(680px,calc(100vh-96px))] md:max-h-none md:w-105 md:rounded-xl",
          isOpen
            ? "translate-y-0"
            : "pointer-events-none translate-y-[110%] md:translate-y-4 md:opacity-0"
        )}
      >
        {/* No rule under the header, and none over the composer: the whole panel
            is one white sheet, so the conversation is the only thing with edges. */}
        <header className="flex shrink-0 items-center justify-between gap-3 px-5 pt-4 pb-2">
          <span className="min-w-0 truncate text-md leading-tight font-medium text-fg">
            {assistantName}
          </span>
          <div className="group relative flex shrink-0 items-center">
            <span
              aria-hidden="true"
              className="pointer-events-none absolute right-full mr-2 rounded-md bg-elevated px-2 py-1 text-sm whitespace-nowrap text-fg opacity-0 shadow-popover ring-1 ring-line-3 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
            >
              Close
            </span>
            <Button
              type="button"
              variant="secondary"
              size="icon-lg"
              aria-label="Close"
              onClick={onClose}
            >
              <X className="size-4" />
            </Button>
          </div>
        </header>

        <div className="relative flex min-h-0 flex-1 flex-col">
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-2"
          >
            {/* A returning client already has a conversation. Showing them how
                to start one before it has been read back would be telling them
                they have never spoken to Casey. */}
            {isRestoring && !hasConversation ? (
              <p className="m-0 text-md text-fg-4">
                Loading your conversation...
              </p>
            ) : null}

            {!hasConversation && !isRestoring ? (
              <div className="flex flex-col gap-3">
                <p className="m-0 text-md text-fg-2">
                  I can explain any question, translate it into your language,
                  or read a document you upload and fill in what I find. For
                  advice about your case,{" "}
                  {attorneyLabel(attorneyName) ?? "your attorney"} is the right
                  person to ask.
                </p>
                <div className="flex flex-col gap-2">
                  {SUGGESTIONS.map((suggestion) => (
                    <button
                      key={suggestion}
                      type="button"
                      onClick={() => onSend(suggestion)}
                      className="flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-left text-md leading-snug text-fg inset-ring inset-ring-line-2 transition-colors hover:bg-fill-1"
                    >
                      <span>{suggestion}</span>
                      <ArrowUpRight
                        className="size-3.5 shrink-0 text-fg-4"
                        aria-hidden
                      />
                    </button>
                  ))}
                </div>
              </div>
            ) : null}

            {messages.map((message, index) => (
              <CaseyMessage
                key={message.id}
                message={message}
                assistantName={assistantName}
                showByline={messages[index - 1]?.role !== "assistant"}
                decisions={decisions}
                onAcceptProposal={onAcceptProposal}
                onRejectProposal={onRejectProposal}
                onAcceptAll={onAcceptAll}
              />
            ))}

            {isStreaming ? <TypingIndicator /> : null}

            {error ? (
              <div className="flex items-start gap-2 rounded-lg bg-danger-soft px-3 py-2.5">
                <AlertCircle
                  className="mt-0.5 size-3.5 shrink-0 text-danger"
                  aria-hidden
                />
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-base leading-snug text-danger">
                    {clientFacingError(error)}
                  </span>
                  <button
                    type="button"
                    onClick={onClearError}
                    className="self-start text-base font-medium text-danger underline underline-offset-2"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {/* Floats over the last of the conversation rather than sitting in the
              flow, so nothing below it moves when the client scrolls up. */}
          {hasConversation && !isAtBottom ? (
            <Button
              type="button"
              variant="primary"
              size="icon-lg"
              aria-label="Jump to the latest message"
              onClick={() => scrollToBottom()}
              className={cn(
                INK_ROUND_BUTTON,
                "absolute bottom-1 left-1/2 -translate-x-1/2 shadow-popover"
              )}
            >
              <ArrowDown />
            </Button>
          ) : null}
        </div>

        <div className="shrink-0 px-5 pt-1 pb-3">
          {attachments.length ? (
            <AttachmentRail attachments={attachments} />
          ) : null}

          {uploadError ? (
            <p className="m-0 mb-2 text-base text-danger">{uploadError}</p>
          ) : null}

          <div className="flex items-center gap-1 rounded-xl px-3 py-2 inset-ring inset-ring-line-2 transition-colors focus-within:inset-ring-line-4">
            {/* Deliberately unfiltered, like the intake's own uploader. An
                `accept` list is only ever as good as the browser's reading of
                it, and one that quietly greys out a client's passport photo
                leaves them with no idea why. What Casey can read is enforced on
                the way out instead, where it can be said in words. */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(event) => {
                handleFiles(event.target.files);
                event.target.value = "";
              }}
            />
            <textarea
              ref={textareaRef}
              value={draft}
              rows={1}
              placeholder="Ask a question"
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  submit();
                }
              }}
              className="max-h-32 min-h-11 flex-1 resize-none border-none bg-transparent px-1 py-3 text-md leading-snug text-fg outline-hidden placeholder:text-fg-4"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon-lg"
              aria-label="Attach a document"
              onClick={() => fileInputRef.current?.click()}
              className="rounded-full"
            >
              <Paperclip className="size-4" />
            </Button>
            {isStreaming ? (
              <Button
                type="button"
                variant="primary"
                size="icon-lg"
                aria-label="Stop"
                onClick={onStop}
                className={INK_ROUND_BUTTON}
              >
                <Square />
              </Button>
            ) : (
              <Button
                type="button"
                variant="primary"
                size="icon-lg"
                aria-label="Send"
                disabled={!draft.trim()}
                onClick={submit}
                className={cn(
                  INK_ROUND_BUTTON,
                  "disabled:bg-fill-2 disabled:text-fg-3 disabled:opacity-100"
                )}
              >
                <ArrowRight className="size-4" />
              </Button>
            )}
          </div>

          <p className="m-0 mt-2 text-center text-sm leading-snug text-fg-4">
            {assistantName} helps you complete this form and cannot give legal
            advice.{lawFirmName ? ` Ask ${lawFirmName} about your case.` : ""}
          </p>
        </div>
      </aside>
    </>
  );
}

function TypingIndicator() {
  return (
    <div className="flex items-center gap-1 px-1 py-1" aria-label="Thinking">
      {[0, 150, 300].map((delay) => (
        <span
          key={delay}
          className="size-1.5 animate-pulse rounded-full bg-fill-4"
          style={{ animationDelay: `${delay}ms` }}
        />
      ))}
    </div>
  );
}

/**
 * Every file the client has attached this session, kept to two rows.
 *
 * The rail lives in the panel's fixed footer, so left to grow it takes the
 * conversation with it: ten files halve the chat, and around twenty the
 * composer is pushed out of the panel and clipped, leaving nowhere to type.
 * The chips are still all here -- a client who uploaded eight documents wants
 * to know they all arrived -- just behind a count until asked for.
 */
const COLLAPSED_ROWS = 2;
/** Matches the rail's `gap-1.5`, which layout maths cannot read off the class. */
const RAIL_GAP_PX = 6;
/** About four rows. Past that the rail scrolls rather than grows. */
const EXPANDED_MAX_HEIGHT_PX = 148;

/** Shared so the control that reveals the rest reads as one of the files. */
const CHIP_CLASS =
  "flex max-w-full items-center gap-1.5 rounded-full bg-fill-1 px-2.5 py-1.5 text-sm font-medium text-fg-2";

function AttachmentRail({ attachments }: { attachments: CaseyAttachment[] }) {
  const railRef = useRef<HTMLDivElement | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);
  const [hiddenFromIndex, setHiddenFromIndex] = useState<number | null>(null);

  // How many chips fit in two rows depends on how long the filenames are and
  // how wide the panel is, so it is measured rather than assumed. Re-run when
  // the list changes -- which includes a chip's label changing from "Reading…"
  // to "3 answers found", since that can rewrap the row.
  useLayoutEffect(() => {
    const measure = () => {
      const node = railRef.current;
      const chips = node ? (Array.from(node.children) as HTMLElement[]) : [];
      if (!chips.length) {
        setCollapsedHeight(null);
        setHiddenFromIndex(null);
        return;
      }
      const rowHeight = chips[0].offsetHeight;
      const rowPitch = rowHeight + RAIL_GAP_PX;
      setCollapsedHeight(
        COLLAPSED_ROWS * rowHeight + (COLLAPSED_ROWS - 1) * RAIL_GAP_PX
      );
      // The top of the first row that will be clipped. A pixel of slack because
      // sub-pixel layout can put a row a hair above where the maths says.
      const cutoff = chips[0].offsetTop + COLLAPSED_ROWS * rowPitch - 1;
      const first = chips.findIndex((chip) => chip.offsetTop >= cutoff);
      setHiddenFromIndex(first === -1 ? null : first);
    };
    measure();
    // The panel is a fixed 420px on desktop and full-bleed on mobile, so the
    // only thing that rewraps it after mount is the viewport changing.
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [attachments]);

  const hiddenCount =
    hiddenFromIndex === null ? 0 : attachments.length - hiddenFromIndex;
  const isClamped = !isExpanded && hiddenCount > 0;

  return (
    <div className="mb-2 flex flex-col gap-1.5">
      <div
        ref={railRef}
        className={cn(
          "flex flex-wrap gap-1.5",
          isExpanded ? "overflow-y-auto" : "overflow-hidden"
        )}
        style={
          isExpanded
            ? { maxHeight: EXPANDED_MAX_HEIGHT_PX }
            : isClamped && collapsedHeight !== null
            ? { maxHeight: collapsedHeight }
            : undefined
        }
      >
        {attachments.map((attachment, index) => (
          <AttachmentChip
            key={attachment.attachment_id}
            attachment={attachment}
            // Clipped, not unmounted: they have to stay laid out for the next
            // measurement to see them. Taking them out of the tab order stops
            // the footer scrolling to a chip nobody can see.
            isClipped={isClamped && index >= (hiddenFromIndex ?? 0)}
          />
        ))}
      </div>

      {hiddenCount > 0 ? (
        <button
          type="button"
          onClick={() => setIsExpanded((prev) => !prev)}
          aria-expanded={isExpanded}
          className={cn(
            CHIP_CLASS,
            "self-start transition-colors hover:bg-fill-2"
          )}
        >
          {isExpanded ? (
            <ChevronUp className="size-3 shrink-0 text-fg-3" aria-hidden />
          ) : (
            <ChevronDown className="size-3 shrink-0 text-fg-3" aria-hidden />
          )}
          <span>{isExpanded ? "Show Fewer" : `+${hiddenCount} More`}</span>
        </button>
      ) : null}
    </div>
  );
}

function AttachmentChip({
  attachment,
  isClipped = false
}: {
  attachment: CaseyAttachment;
  /** Laid out but cut off by the collapsed rail, so hidden from AT and tabbing. */
  isClipped?: boolean;
}) {
  const { attachment_id: id, extraction_status: status } = attachment;
  const found = attachment.candidate_count;
  const failed = status === EXTRACTION_FAILED;
  const detail =
    status === EXTRACTION_READY
      ? found
        ? `${found} answer${found === 1 ? "" : "s"} found`
        : "Read"
      : failed
      ? "Couldn't read it"
      : "Reading…";

  const viewer = useCitationViewer();
  // Fetched as soon as the server says a copy exists, so the chip only offers
  // to open what it can actually show, and opens it without a wait.
  const document = useCitationDocument(
    id,
    Boolean(attachment.has_citation_pdf)
  );
  const isOpen = viewer.openCitationId === id;

  const content = (
    <>
      {/* Whatever kind of file it is, drawn the way the intake's own uploaded
          files are, rather than as a generic page. */}
      <FileTypeIcon name={attachment.filename} size={13} />
      <span className="max-w-40 truncate font-medium">
        {attachment.filename}
      </span>
      <span className={cn("shrink-0", failed ? "text-danger" : "text-fg-4")}>
        {detail}
      </span>
    </>
  );

  if (document.status !== "ready") {
    return (
      <span className={CHIP_CLASS} aria-hidden={isClipped || undefined}>
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      onClick={() =>
        viewer.toggle({
          citationId: id,
          file: document.url,
          filename: attachment.filename
        })
      }
      aria-pressed={isOpen}
      aria-hidden={isClipped || undefined}
      tabIndex={isClipped ? -1 : undefined}
      aria-label={`View ${attachment.filename}`}
      className={cn(
        CHIP_CLASS,
        "transition-colors hover:bg-fill-2",
        isOpen && "ring-1 ring-line-4"
      )}
    >
      {content}
      {isOpen ? (
        <X className="size-3 shrink-0 text-fg-3" aria-hidden />
      ) : (
        <Maximize2 className="size-3 shrink-0 text-fg-3" aria-hidden />
      )}
    </button>
  );
}

type MessageProps = {
  message: CaseyUIMessage;
  assistantName: string;
  /** Over the first reply of a turn only. */
  showByline: boolean;
  decisions: Record<string, ProposalDecision>;
  onAcceptProposal: (proposalId: string) => void;
  onRejectProposal: (proposalId: string) => void;
  onAcceptAll: (proposalIds: string[]) => void;
};

function CaseyMessage({
  message,
  assistantName,
  showByline,
  decisions,
  onAcceptProposal,
  onRejectProposal,
  onAcceptAll
}: MessageProps) {
  if (message.role === "user") {
    const text = message.parts
      .filter((part) => part.type === "text")
      .map((part) => (part as { text: string }).text)
      .join("");
    if (!text) return null;
    return (
      <div className="flex justify-end" data-testid="casey-user-message">
        {/* Only the client's own words get a bubble. Casey's replies run as
            plain text on the sheet, which is what lets an answer be long
            without looking like a wall of tinted background. */}
        <div className="max-w-4/5 rounded-xl bg-accent-soft/60 px-3.5 py-2.5 text-md wrap-break-word whitespace-pre-wrap text-fg dark:bg-accent-soft">
          {text}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="casey-assistant-message">
      {showByline ? <AssistantByline name={assistantName} /> : null}
      {message.parts.map((part, index) => (
        <AssistantPart
          // Parts have no stable id of their own, and their order within a
          // message is fixed once streamed.
          key={`${message.id}-${index}`}
          part={part}
          decisions={decisions}
          onAcceptProposal={onAcceptProposal}
          onRejectProposal={onRejectProposal}
          onAcceptAll={onAcceptAll}
        />
      ))}
    </div>
  );
}

function AssistantPart({
  part,
  decisions,
  onAcceptProposal,
  onRejectProposal,
  onAcceptAll
}: {
  part: CaseyUIMessage["parts"][number];
  decisions: Record<string, ProposalDecision>;
  onAcceptProposal: (proposalId: string) => void;
  onRejectProposal: (proposalId: string) => void;
  onAcceptAll: (proposalIds: string[]) => void;
}) {
  if (part.type === "text") {
    if (!part.text?.trim()) return null;
    return (
      <div className="max-w-full text-md wrap-break-word text-fg [&_a]:underline [&_li]:my-1.5 [&_ol]:my-2.5 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-2.5 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_ul]:my-2.5 [&_ul]:list-disc [&_ul]:pl-5">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{part.text}</ReactMarkdown>
      </div>
    );
  }

  if (part.type === "data-casey-progress") {
    return (
      <span className="flex items-center gap-1.5 text-base font-medium text-fg-3">
        <Spinner className="size-3.5" />
        {part.data?.label}
      </span>
    );
  }

  if (part.type === "data-referral") {
    return (
      <div className="flex items-start gap-2.5 rounded-lg bg-warn-soft px-3 py-2.5 ring-1 ring-warn-line">
        <Scale className="mt-0.5 size-3.5 shrink-0 text-warn" aria-hidden />
        <span className="text-base leading-snug text-fg">
          This one is for {referralWho(part.data)} to answer.
        </span>
      </div>
    );
  }

  if (part.type === "data-proposals") {
    const proposals: CaseyProposal[] = part.data?.proposals ?? [];
    if (!proposals.length) return null;
    const pending = proposals.filter(
      (proposal) => (decisions[proposal.proposal_id] ?? "pending") === "pending"
    );
    return (
      <div className="flex flex-col gap-2 rounded-lg bg-fill-1 p-2.5">
        <div className="flex items-center justify-between gap-2 px-0.5">
          <span className="text-base font-medium text-fg">
            {proposals.length === 1
              ? "Suggested Answer"
              : `${proposals.length} Suggested Answers`}
          </span>
          {pending.length > 1 ? (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() =>
                onAcceptAll(pending.map((proposal) => proposal.proposal_id))
              }
            >
              Use All
            </Button>
          ) : null}
        </div>
        {proposals.map((proposal) => (
          <ProposalCard
            key={proposal.proposal_id}
            proposal={proposal}
            decision={decisions[proposal.proposal_id] ?? "pending"}
            onAccept={() => onAcceptProposal(proposal.proposal_id)}
            onReject={() => onRejectProposal(proposal.proposal_id)}
          />
        ))}
      </div>
    );
  }

  return null;
}
